// src/pages/HomeDashboard.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useApi } from "../apiClient";
import EditActivityModal from "../components/EditActivityModal";
import { motion, AnimatePresence } from "framer-motion";
import formatDate from "../utils/formatDate";
import {
  addMonths, subMonths, startOfMonth, isSameMonth, format, parseISO,
} from "date-fns";
import { useLang } from "../contexts/LanguageContext";
import locales from "../utils/locales";

const categories = [
  { value: "work", label: { en: "Work", th: "งาน" }, color: "bg-blue-400" },
  { value: "family", label: { en: "Family", th: "ครอบครัว" }, color: "bg-pink-400" },
  { value: "health", label: { en: "Health", th: "สุขภาพ" }, color: "bg-green-400" },
  { value: "learning", label: { en: "Learning", th: "การเรียนรู้" }, color: "bg-yellow-400" },
  { value: "rest", label: { en: "Rest", th: "พักผ่อน" }, color: "bg-gray-400" },
  { value: "other", label: { en: "Other", th: "อื่น ๆ" }, color: "bg-purple-400" },
];

function MiniMonthBar({ month, setMonth, lang }) {
  const months = [
    subMonths(month, 1),
    month,
    addMonths(month, 1),
  ];
  return (
    <div className="sticky top-14 z-20 bg-gray-50 flex justify-center items-center gap-3 py-3 border-b border-gray-100">
      <button
        onClick={() => setMonth(subMonths(month, 1))}
        className="text-xl px-2"
        aria-label={lang === "th" ? "เดือนก่อนหน้า" : "Previous month"}
      >‹</button>
      {months.map((m, i) => (
        <button
          key={i}
          className={
            "px-4 py-2 rounded-full font-semibold transition " +
            (isSameMonth(m, month)
              ? "bg-indigo-500 text-white scale-105 shadow"
              : "bg-gray-100 text-gray-700 hover:bg-indigo-100")
          }
          onClick={() => setMonth(m)}
        >
          {format(m, "MMM yyyy")}
        </button>
      ))}
      <button
        onClick={() => setMonth(addMonths(month, 1))}
        className="text-xl px-2"
        aria-label={lang === "th" ? "เดือนถัดไป" : "Next month"}
      >›</button>
    </div>
  );
}

export default function HomeDashboard() {
  const api = useApi();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(startOfMonth(new Date()));

  const { lang } = useLang();
  const t = locales[lang];

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.activities.list();
      setActivities(data || []);
    } catch {
      setActivities([]);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const filteredByMonth = useMemo(() => {
    return activities.filter((a) => {
      const d = a.activity_date || a.created_at?.slice(0, 10);
      const actDate = parseISO(d);
      return isSameMonth(actDate, selectedMonth);
    });
  }, [activities, selectedMonth]);

  const filteredActivities = filteredByMonth
    .filter(a => categoryFilter === "all" || a.category === categoryFilter)
    .filter(a => a.activity?.toLowerCase().includes(search.toLowerCase()));

  const handleSaveEdit = async (updated) => {
    await api.activities.update(updated.id, {
      activity: updated.activity,
      duration: updated.duration,
      note: updated.note,
      mood: updated.mood,
      category: updated.category,
      activity_date: updated.activity_date,
    });
    setEditModalOpen(false);
    setSelectedActivity(null);
    fetchActivities();
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.confirm_delete)) {
      await api.activities.delete(id);
      fetchActivities();
    }
  };

  return (
    <motion.div
      className="max-w-2xl mx-auto p-4 mt-0 bg-gray-50 min-h-[80vh] transition-colors duration-300"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* MINI MONTH BAR (Sticky) */}
      <MiniMonthBar month={selectedMonth} setMonth={setSelectedMonth} lang={lang} />

      {/* SEARCH + FILTER BAR */}
      <div className="mb-4">
        <input
          className="border rounded-xl px-3 py-1 mb-2 w-full transition-colors"
          type="text"
          placeholder={t.search}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            className={`px-3 py-1 rounded-full font-semibold transition ${categoryFilter === "all"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-700"}`}
            onClick={() => setCategoryFilter("all")}
          >{t.all}</button>
          {categories.map(c => (
            <button
              key={c.value}
              className={`px-3 py-1 rounded-full font-semibold transition ${categoryFilter === c.value
                ? `${c.color} text-white`
                : "bg-gray-100 text-gray-700"}`}
              onClick={() => setCategoryFilter(c.value)}
            >{c.label[lang]}</button>
          ))}
        </div>
      </div>

      {/* LIST ACTIVITIES */}
      {loading ? (
        <div className="text-center text-gray-400 py-10">{t.loading}</div>
      ) : (
        <ul className="space-y-3">
          <AnimatePresence>
            {filteredActivities.map(a => {
              const cat = categories.find(c => c.value === a.category);
              // Mapping emoji เป็น label multi-language
              const moodLabel =
                a.mood === "😊" ? t.mood_happy :
                a.mood === "😌" ? t.mood_calm :
                a.mood === "😐" ? t.mood_neutral :
                a.mood === "😔" ? t.mood_sad :
                a.mood === "😠" ? t.mood_angry : "";
              return (
                <motion.li
                  key={a.id}
                  initial={{ opacity: 0, y: 18, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 15, scale: 0.97 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                  className="bg-white rounded-lg shadow p-4 flex justify-between items-center transition-colors"
                  layout
                >
                  <div>
                    <div className="flex items-center gap-2">
                      {/* Mood Emoji (แสดงเฉพาะถ้ามี mood) */}
                      {a.mood && (
                        <span
                          className="text-2xl"
                          title={moodLabel}
                          aria-label={moodLabel}
                          style={{ minWidth: 32, textAlign: "center" }}
                        >
                          {a.mood}
                        </span>
                      )}
                      <span className="font-bold text-lg text-gray-900">{a.activity}</span>
                      {cat && (
                        <span className={`inline-block px-2 py-1 text-xs rounded ${cat.color} text-white font-semibold`}>
                          {cat.label[lang]}
                        </span>
                      )}
                      {a.is_recurring && (
                        <span className="inline-block px-2 py-1 text-xs rounded bg-amber-400 text-white font-semibold ml-1 flex items-center gap-1">
                          <svg width="13" height="13" fill="none" className="inline-block" viewBox="0 0 13 13"><path d="M2 6.5A4.5 4.5 0 1111 6.5M2 6.5h3m-3 0v3" stroke="currentColor" strokeWidth="1.2" /></svg>
                          {t.recurring}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{a.duration}</div>
                    {a.note && <div className="text-gray-700 mt-1">{a.note}</div>}
                    <div className="text-xs text-gray-400 mt-2">
                      {formatDate(a.activity_date || a.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditModalOpen(true); setSelectedActivity(a); }}
                      className="text-blue-500 hover:underline"
                      title={t.edit}
                    >✏️</button>
                    <button
                      onClick={() => handleDelete(a.id)}
                      className="text-red-500 hover:underline"
                      title={t.delete}
                    >🗑️</button>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
      <AnimatePresence>
        {editModalOpen && selectedActivity && (
          <motion.div
            key="edit-modal"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.32 }}
            className="z-50"
            style={{
              position: "fixed",
              left: 0, top: 0, right: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(0,0,0,0.26)",
            }}
          >
            <EditActivityModal
              show={editModalOpen}
              onClose={() => { setEditModalOpen(false); setSelectedActivity(null); }}
              activity={selectedActivity}
              onSave={handleSaveEdit}
              categories={categories}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}